import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as S from '../scss/theme';
import fnc from '../mymod/commFunction';
import { setHeaderInfo } from '../reduxToolkit/redux';
import AttendanceCalendar from './components/AttendanceCalendar';
import LearningDetails from './components/LearningDetails';
import ReportInsights from './components/ReportInsights';
import ReportToolbar from './components/ReportToolbar';
import SummaryGrid from './components/SummaryGrid';
import {
  getPeriodRange,
  getPeriodTitle,
  moveAnchorDate,
  normalizeReportRows,
  parseDate,
  summarizeReport,
} from './reportUtils';
import './LmsLearningReport.scss';

const LmsLearningReport = () => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState('day');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [classCode, setClassCode] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const range = useMemo(() => getPeriodRange(mode, anchorDate), [mode, anchorDate]);
  const summary = useMemo(() => summarizeReport(rows), [rows]);
  const periodTitle = useMemo(() => getPeriodTitle(mode, range), [mode, range]);

  useEffect(() => {
    dispatch(setHeaderInfo({ title: '학습 리포트', direction: -1 }));
    loadClasses();
  }, [dispatch]);

  useEffect(() => {
    if (!classCode) return;
    loadReport();
  }, [classCode, range.startDate, range.endDate]);

  function loadClasses() {
    const acaCode = fnc.getCookie('acaCode');
    const stCode = fnc.getCookie('stCode');

    fnc.sendAxios('/select/lms/treeview', { acaCode, stCode }, (res) => {
      if (!Array.isArray(res)) {
        setError('수강 강좌를 불러오지 못했습니다.');
        return;
      }

      const uniqueClasses = Array.from(
        new Map(res.map((item) => [item.clClassCode, item])).values(),
      );

      setClasses(uniqueClasses);
      setClassCode((current) => current || uniqueClasses[0]?.clClassCode || '');
    });
  }

  function loadReport() {
    const acaCode = fnc.getCookie('acaCode');
    const stCode = fnc.getCookie('stCode');

    setLoading(true);
    setError('');

    fnc.sendAxios(
      '/select/lms/report/student',
      {
        acaCode,
        stCode,
        classCode,
        startDate: range.startDate,
        endDate: range.endDate,
      },
      (res) => {
        if (Array.isArray(res)) {
          setRows(normalizeReportRows(res));
        } else {
          setRows([]);
          setError(res?.error || '학습 리포트를 불러오지 못했습니다.');
        }
        setLoading(false);
      },
    );
  }

  function handleAnchorDateChange(value) {
    if (!value) return;
    setAnchorDate(parseDate(value));
  }

  function handleMove(direction) {
    setAnchorDate((current) => moveAnchorDate(mode, current, direction));
  }

  return (
    <S.Section>
      <main className="lms-report">
        <header className="lms-report__header">
          <div>
            <h1>학습 리포트</h1>
            <span>진도가 등록된 날짜를 기준으로 학습 상태를 확인합니다.</span>
          </div>
        </header>

        {!!classes.length && (
          <ReportToolbar
            mode={mode}
            onModeChange={setMode}
            anchorDate={anchorDate}
            onAnchorDateChange={handleAnchorDateChange}
            onMove={handleMove}
            periodTitle={periodTitle}
            classes={classes}
            classCode={classCode}
            onClassChange={setClassCode}
          />
        )}

        {!classes.length && !error && (
          <div className="lms-report__empty">수강 중인 강좌가 없습니다.</div>
        )}

        {error && <div className="lms-report__error">{error}</div>}

        {!!classes.length && (
          <div className={`lms-report__content ${loading ? 'is-loading' : ''}`}>
            {loading && <div className="lms-report__loading">학습 상태를 불러오는 중입니다.</div>}
            <SummaryGrid summary={summary} />
            <AttendanceCalendar mode={mode} anchorDate={anchorDate} rows={rows} />
            <LearningDetails rows={rows} />
            <ReportInsights summary={summary} rows={rows} />
          </div>
        )}
      </main>
    </S.Section>
  );
};

export default LmsLearningReport;
